import React, { useCallback, useState, useRef } from "react";
import { useMediaQuery } from "@react-hook/media-query";
import { DESKTOP_M_BP } from "@website-builder/utilities/constants/style-constants/screen-sizes.js";

import {
	Card,
	Text,
	CloudinaryImage,
	VerticalTab,
	VerticalTabItem,
	Accordion,
	Link,
	LineGraphic,
} from "@website-builder/ui/shared/elements";

import {
	MentorGridWrapper,
	MentorItemImageWrapper,
	MentorItemContentWrapper,
	MentorItemCompanyLogoWrapper,
	AccordionItemHeadingWrapper,
	MentorsCardsWrapper,
	LoadMoreCTAWrapper,
} from "./styles";

const MentorGrid = ({
	mentorGridCourse,
	mentorGridHeadline,
	mentorGridFilterTitle,
	loadMoreLabel,
	initialInViewMentorCount,
}) => {
	//state
	const [courseDetails, setMentorGridCourseState] = useState(mentorGridCourse);
	const isDesktopLessThan1200 = useMediaQuery(`(max-width:${DESKTOP_M_BP}px)`);

	// content reference ti enable scroll back to top
	const contentReference = useRef(null);

	const onChange = () => {
		contentReference?.current?.scrollIntoView({ behavior: "smooth" });
	};
	// show/load more mentors and updates the in view mentor
	const showMoreMentors = useCallback(
		(courseName) => {
			const updatedCourseDetails = courseDetails.map((course) => {
				if (course?.courseName === courseName) {
					return {
						...course,
						inViewMentorsCount:
							course?.inViewMentorsCount + initialInViewMentorCount,
					};
				}
				return course;
			});
			setMentorGridCourseState(updatedCourseDetails);
		},
		[courseDetails],
	);
	// Mentor Grid Content with all the Mentor Cards
	const MentorGridContent = React.memo((course) => {
		return (
			<>
				<MentorsCardsContainer {...course} />
				{course?.inViewMentorsCount < course?.mentorGridCourseItem.length && (
					<LoadMoreCTAWrapper>
						<Link
							onClick={() => showMoreMentors(course?.courseName)}
							iconRight
							iconConfig={{ iconName: "caretDown", color: "#5E0BD1" }}
							linkText={loadMoreLabel}
							className="show-more-mentors-link"
							as="button"
						/>
					</LoadMoreCTAWrapper>
				)}
			</>
		);
	});

	// Mobile View Accordion Component
	const AccordionHeading = ({ heading }) => {
		return (
			<AccordionItemHeadingWrapper>
				<Text
					color={{
						base: "primary",
						color: "neutral-nightshade",
						shade: "800",
					}}
					variant="headline_5"
					tag="h3"
				>
					{heading}
				</Text>
			</AccordionItemHeadingWrapper>
		);
	};
	// generates accordion items array for <Accordion/>
	const generateAccordionDetails = () => {
		return courseDetails.map((course) => {
			return {
				heading: <AccordionHeading heading={course?.courseName} />,
				content: <MentorGridContent {...course} />,
			};
		});
	};

	const MentorProfileCard = ({
		name,
		position,
		companyLogo,
		profileImage,
		courseName,
	}) => {
		return (
			<Card className="mentor-grid-item" data-course-name={courseName}>
				<MentorItemImageWrapper>
					<CloudinaryImage
						className="mentor-profile-image"
						url={profileImage?.[0]?.image?.image}
						alt={profileImage?.[0]?.image?.altTag}
						quality="auto"
						lazyload
						fetchFormat="auto"
						dynamic
					/>
				</MentorItemImageWrapper>
				<MentorItemContentWrapper>
					<div className="mentor-item-text-wrapper">
						<Text
							color={{
								base: "primary",
								color: "neutral-nightshade",
								shade: "800",
							}}
							variant="headline_6"
							tag="p"
						>
							{name}
						</Text>
						{position ? (
							<Text
								color={{
									base: "primary",
									color: "neutral-nightshade",
									shade: "600",
								}}
								variant="paragraph_M"
							>
								{position}
							</Text>
						) : null}
					</div>
					<MentorItemCompanyLogoWrapper>
						<CloudinaryImage
							className="mentor-company-logo"
							url={companyLogo[0]?.image?.image}
							alt={companyLogo?.[0]?.image?.altTag}
							quality="auto"
							lazyload
							fetchFormat="auto"
							dynamic
						/>
					</MentorItemCompanyLogoWrapper>
				</MentorItemContentWrapper>
			</Card>
		);
	};

	//Mentor Cards Container
	const MentorsCardsContainer = ({
		mentorGridCourseItem,
		courseName,
		inViewMentorsCount,
	}) => {
		return (
			<MentorsCardsWrapper>
				{mentorGridCourseItem
					.slice(0, inViewMentorsCount)
					.map((mentor, index) => {
						return (
							<MentorProfileCard
								{...mentor}
								courseName={courseName}
								key={index}
							/>
						);
					})}
			</MentorsCardsWrapper>
		);
	};

	return (
		<MentorGridWrapper
			className="mentor-filterable-grid"
			ref={contentReference}
		>
			<LineGraphic
				className="course-line-graphic"
				stroke="var(--primary-green-600)"
				size="30vw"
				right="0"
				top="0"
				x="20%"
				y="10%"
				rotate="50"
				line="double_switch"
			/>
			{mentorGridHeadline ? (
				<div className="mentor-grid-title-wrapper text-center ">
					<Text
						variant="headline_2"
						color={{
							base: "primary",
							color: "neutral-nightshade",
							shade: "800",
						}}
					>
						{mentorGridHeadline}
					</Text>
				</div>
			) : null}
			<VerticalTab
				className="mentor-grid-vertical-tabs"
				tabSpan={isDesktopLessThan1200 ? [1, 3] : [1, 2]}
				contentSpan={isDesktopLessThan1200 ? [4, 12] : [4, 12]}
				heading={mentorGridFilterTitle}
				onChange={onChange}
			>
				{courseDetails.map((course, index) => {
					return (
						<VerticalTabItem
							name={course?.courseName}
							key={`${course?.courseName}+${index}`}
						>
							<MentorGridContent {...course} />
						</VerticalTabItem>
					);
				})}
			</VerticalTab>
			{/* Mobile view */}
			<div className="mentor-grid-accordion">
				<Text
					className="accordion-heading text-center"
					variant="label_L"
					color={{
						base: "secondary",
						color: "gray",
						shade: "dark",
					}}
				>
					{mentorGridFilterTitle}
				</Text>
				<Accordion
					showTopSeparator={true}
					showItemTopSeparator={false}
					accordionContentClassName="mentor-grid-accordion-content"
					icon="plusMinus"
					accordionItems={generateAccordionDetails()}
				/>
			</div>
		</MentorGridWrapper>
	);
};

export default MentorGrid;
